<template>
  <div class="lf-calendar">
    <VueDatePicker
      v-model="dateValue"
      inline
      auto-apply
      range
      :multi-calendars="multiCalendar"
      :month-picker="monthPicker"
      :enable-time-picker="false"
      :month-change-on-scroll="false"
      light
      format="MM/dd/yyyy"
      ref="calendarRef"
      @update:model-value="$emit('update:modelValue', $event)"
    />
  </div>
</template>

<script setup lang="ts">
import { type PropType, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import VueDatePicker from "@vuepic/vue-datepicker";
import { isValid } from "date-fns";

import { useNotification } from "@/hooks/notifications";

type MonthYearObject = { month: number; year: number };
type DateValue = Date | string | MonthYearObject;
type DateArray = DateValue[];

const FIRST_DAY_OF_MONTH = 1;

const props = defineProps({
  modelValue: {
    type: Array as PropType<DateArray>,
    required: true,
  },
  monthPicker: {
    type: Boolean,
    default: false,
  },
  multiCalendar: {
    type: Boolean,
    default: false,
  },
});

defineEmits<{
  "update:modelValue": [date: DateArray];
}>();

const isDateObject = (val: unknown): val is Date => val instanceof Date;
const isMonthYearObject = (val: unknown): val is MonthYearObject =>
  typeof val === "object" && val !== null && "month" in val && "year" in val;
const isStringDate = (val: unknown): val is string => typeof val === "string";

const { showMessage } = useNotification();
const { t } = useI18n();

const calendarRef = ref<InstanceType<typeof VueDatePicker> | null>(null);

const dateValue = ref<Date[] | string[] | MonthYearObject[]>([]);

const createDefaultMonthYear = (): MonthYearObject => ({
  month: 0,
  year: new Date().getFullYear(),
});

const convertToDate = (val: DateValue): Date | null => {
  if (isDateObject(val)) {
    return val;
  }
  if (isStringDate(val)) {
    const parsed = new Date(val);
    return isValid(parsed) ? parsed : null;
  }
  return null;
};

const getMonthYear = (val: DateValue): MonthYearObject => {
  if (isMonthYearObject(val)) {
    return val;
  }

  const date = convertToDate(val);
  if (!date) {
    const errorKey = isStringDate(val)
      ? "ERRORS.INVALID_DATE_FORMAT"
      : "ERRORS.INVALID_DATE_VALUE";
    showMessage(t(errorKey), "error");
    return createDefaultMonthYear();
  }

  return { month: date.getMonth(), year: date.getFullYear() };
};

const normalizeValue = (val: DateArray, monthPicker: boolean): DateArray => {
  if (!Array.isArray(val) || !val.length) {
    return [];
  }

  if (!monthPicker) {
    return val;
  }

  const start = getMonthYear(val[0]);
  if (!val[1]) {
    return [start];
  }

  return [start, getMonthYear(val[1])];
};

const convertToTargetDate = (val: DateValue): Date | null => {
  if (isMonthYearObject(val)) {
    return new Date(val.year, val.month, FIRST_DAY_OF_MONTH);
  }
  return convertToDate(val);
};

const setMonthYearIfValid = (val: DateArray): void => {
  if (!val?.length) {
    return;
  }

  const targetDate = convertToTargetDate(val[0]);
  if (!targetDate || !isValid(targetDate)) {
    showMessage(t("ERRORS.INVALID_DATE_FOR_CALENDAR"), "error");
    return;
  }

  calendarRef.value?.setMonthYear({
    month: targetDate.getMonth(),
    year: targetDate.getFullYear(),
  });
};

watch(
  () => props.modelValue,
  (newVal) => {
    const normalized = normalizeValue(newVal, props.monthPicker);
    dateValue.value = normalized as Date[] | string[] | MonthYearObject[];
    setMonthYearIfValid(normalized);
  },
  { immediate: true }
);
</script>
<style src="@vuepic/vue-datepicker/dist/main.css" />
<style>
@reference "@/assets/tailwind.css";

.lf-calendar .dp__theme_light {
  --dp-menu-border-color: transparent;
  --dp-text-color: #545464;
  @apply font-sans;
}
.lf-calendar .dp__flex_display {
  @apply flex gap-4;
}
.lf-calendar .dp__month_year_select {
  @apply text-xl font-light uppercase;
}
.lf-calendar .dp__calendar_header_item {
  @apply font-light text-gray-400;
}
.lf-calendar .dp__calendar_item {
  @apply font-light text-gray-900;
}
.lf-calendar .dp__range_start {
  @apply rounded-l-full;
}
.lf-calendar .dp__range_end {
  @apply rounded-r-full;
}
.lf-calendar .dp__range_between {
  @apply border-none bg-blue-300/20;
}
.lf-calendar .dp__overlay_cell {
  @apply border text-xs font-semibold text-label uppercase;
}
.lf-calendar .dp__overlay_cell_active,
.lf-calendar .dp__cell_in_between {
  @apply border border-transparent bg-primary/10 text-xs font-semibold text-primary uppercase;
}
</style>
